import { GroupedOptions, SingleOption } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export type PossibleTriggerGroup = 'status_update' | 'action' | 'other';

export interface PossibleTriggerDisplay {
    group: PossibleTriggerGroup;
    display: React.ReactNode;
}

export interface PossibleTriggerOptionItem<T> {
    group: PossibleTriggerGroup;
    option: SingleOption<T>;
}

export const PossibleTriggerLabels = {
    GROUPS: {
        STATUS_UPDATES: (
            <FormattedMessage
                id='platform.workflow.possible-trigger-labels.group-status-updates'
                description='Group header for status-update options in the Change status dropdown'
                defaultMessage='Status updates'
            />
        ),
        ACTIONS: (
            <FormattedMessage
                id='platform.workflow.possible-trigger-labels.group-actions'
                description='Group header for action options in the Change status dropdown'
                defaultMessage='Actions'
            />
        ),
        OTHER: (
            <FormattedMessage
                id='platform.workflow.possible-trigger-labels.group-other'
                description='Group header for unmapped options in the Change status dropdown'
                defaultMessage='Other'
            />
        )
    },
    PICK_UP: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.pick-up'
            description='Change status option that starts pick up'
            defaultMessage='Pick up'
        />
    ),
    DROP_OFF: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.drop-off'
            description='Change status option that starts drop off'
            defaultMessage='Drop off'
        />
    ),
    CANCEL: CommonMessage.BUTTONS.CANCEL,
    COMPLETE: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.complete'
            description='Change status option that completes the entity'
            defaultMessage='Complete'
        />
    ),
    MARK_READY_FOR_DROP_OFF: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-ready-for-drop-off'
            description='Change status option that marks the entity ready for drop-off'
            defaultMessage='Mark as ready for drop-off'
        />
    ),
    MARK_DROPPED_OFF: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-dropped-off'
            description='Change status option that marks the entity as dropped off'
            defaultMessage='Mark as dropped off'
        />
    ),
    MARK_PICKED_UP: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-picked-up'
            description='Change status option that marks the entity as picked up'
            defaultMessage='Mark as picked up'
        />
    ),
    MARK_PICKED_UP_IN_TRANSIT: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-picked-up-in-transit'
            description='Change status option that marks the entity as picked up and in transit'
            defaultMessage='Mark as picked up (in transit)'
        />
    ),
    MARK_PICKED_UP_AWAITING_DROP_OFF: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-picked-up-awaiting-drop-off'
            description='Change status option that marks the entity as picked up and awaiting drop-off'
            defaultMessage='Mark as picked up (awaiting drop-off)'
        />
    ),
    MARK_CANCELLED: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-cancelled'
            description='Change status option that marks the entity as cancelled'
            defaultMessage='Mark as cancelled'
        />
    ),
    MARK_FAILED: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-failed'
            description='Change status option that marks the entity as failed'
            defaultMessage='Mark as failed'
        />
    ),
    MARK_COMPLETED: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-completed'
            description='Change status option that marks the entity as completed'
            defaultMessage='Mark as completed'
        />
    ),
    MARK_COMPLETED_WITH_FAILURES: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-completed-with-failures'
            description='Change status option that marks the entity as completed with failures'
            defaultMessage='Mark as completed with failures'
        />
    ),
    MARK_PICK_UP_DONE: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-pick-up-done'
            description='Change status option that marks pick-up as done'
            defaultMessage='Mark pick-up as done'
        />
    ),
    MARK_LOST: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-lost'
            description='Change status option that marks the entity as lost'
            defaultMessage='Mark as lost'
        />
    ),
    MARK_PICKED_UP_PICK_UP_DONE: (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.mark-picked-up-pick-up-done'
            description='Change status option that marks the entity as picked up with pick-up done'
            defaultMessage='Mark as picked up (pick-up done)'
        />
    )
} as const;

export function fallbackPossibleTriggerDisplay(triggerDisplay: React.ReactNode, nextStateDisplay: React.ReactNode | null): React.ReactNode {
    if (nextStateDisplay == null) {
        return triggerDisplay;
    }

    return (
        <FormattedMessage
            id='platform.workflow.possible-trigger-labels.fallback'
            description='Fallback Change status option showing trigger and resulting next state'
            defaultMessage='{trigger} → {nextState}'
            values={{ trigger: triggerDisplay, nextState: nextStateDisplay }}
        />
    );
}

const GROUP_ORDER: PossibleTriggerGroup[] = ['status_update', 'action', 'other'];

const GROUP_HEADERS: Record<PossibleTriggerGroup, { label: string; display: React.ReactNode }> = {
    status_update: { label: 'status_updates', display: PossibleTriggerLabels.GROUPS.STATUS_UPDATES },
    action: { label: 'actions', display: PossibleTriggerLabels.GROUPS.ACTIONS },
    other: { label: 'other', display: PossibleTriggerLabels.GROUPS.OTHER }
};

export function groupPossibleTriggerOptions<T>(items: PossibleTriggerOptionItem<T>[]): GroupedOptions<T>[] {
    return GROUP_ORDER.reduce<GroupedOptions<T>[]>((groups, groupKey) => {
        const options = items.filter((item) => item.group === groupKey).map((item) => item.option);
        if (options.length === 0) {
            return groups;
        }

        const header = GROUP_HEADERS[groupKey];
        groups.push({
            label: header.label,
            display: header.display,
            options
        });
        return groups;
    }, []);
}
